import React, { useCallback, useMemo } from "react";
import { WalletSVGIcon } from "@customer-gallery/components/icons";
import { WalletIcon } from "@novel/shared/components/ConnectWalletComponent/WalletIcon";
import { customerRoutePush } from "@customer-gallery/definitions/routeDefinitions";
import omit from "lodash/omit";
import trimEnd from "lodash/trimEnd";
import trimStart from "lodash/trimStart";
import { Box } from "@novel/shared/components/Box";
import { CogIcon } from "@heroicons/react/solid";
import { ItemProps } from "@shopify/polaris/build/ts/latest/src/components/Navigation/components/Item/Item";
import { customerGalleryPrefix } from "@novel/shared/utils/appConstants";
import { Badge, Navigation } from "@shopify/polaris";
import { stringify } from "qs";
import { ITokenWithMetadataAndCollection } from "@novel/shared/interfaces/Token";
import { ICryptoAddressWithTokensAndWallets } from "@novel/shared/interfaces/CryptoAddress";
import { INovelUser } from "@novel/shared/interfaces/NovelUser";
import Image from "next/legacy/image";
import Logo from "@customer-gallery-public/logo.svg";
import { groupToKeyMap } from "@novel/shared/utils/groupToKeyMap";
import { FixedDimensionMedia } from "@novel/shared/components/FixedDimensionMedia";
import LeftNavigation from "./LeftNavigation";
import { shortenCryptoAddress } from "@novel/shared/utils/shortenCryptoAddress";
import { useRouter } from "next/router";
import { useNovelCustomerDispatch } from "@customer-gallery/redux/reduxHooks";
import { BigIntStr } from "@novel/shared/interfaces/shared/BigIntStr";
interface NavSection {
  title?: string | undefined;
  separator?: boolean | undefined;
  navItems: (ItemProps & {
    isDefault?: boolean;
  })[];
}
const LeftNavigationPane = ({
  addresses,
  user,
  mobile,
  userDisplayName,
  customerId,
  onClickClaimed
}: {
  readonly addresses: ICryptoAddressWithTokensAndWallets[];
  readonly user?: INovelUser | null;
  readonly userDisplayName: string;
  readonly mobile?: boolean;
  readonly customerId?: string;
  readonly onClickClaimed: (claimedToken: undefined | ITokenWithMetadataAndCollection) => void;
}): JSX.Element => {
  const router = useRouter();
  const dispatch = useNovelCustomerDispatch();
  const onNavigationToggle = useCallback(() => {
    dispatch({
      type: "CHANGE_SIDEBAR_STATUS",
      payload: {
        open: false
      }
    });
  }, [dispatch]);
  const cleanedRoute = customerId ? `${trimStart(trimEnd(router.pathname, "/").replace(`[customerId]`, `/`), "/") || ""}${stringify(omit(router.query, "customerId"), {
    addQueryPrefix: true
  })}` : `${trimStart(trimEnd(router.pathname, "/").replace(customerGalleryPrefix, ""), "/") || ""}${stringify(router.query, {
    addQueryPrefix: true
  })}`;
  const finalizeNavSection: (navSection: NavSection) => NavSection = useCallback(({
    title,
    separator,
    navItems
  }: NavSection): NavSection => ({
    title,
    separator,
    navItems: navItems.map(navItem => {
      const url = new URL(`${process.env.NEXT_PUBLIC_HOST}${customerGalleryPrefix}${navItem.url!}`);
      const cleanedNavPath = customerId ? `${trimStart(trimEnd(url.pathname, "/").replace(`${customerGalleryPrefix}/${customerId}`, `/`), "/")}${url.search}` : `${trimStart(trimEnd(url.pathname, "/").replace(customerGalleryPrefix, ""), "/")}${url.search}`;
      const isOnBasePath = !!navItem.isDefault && cleanedRoute === "";
      const selected = cleanedRoute === cleanedNavPath || isOnBasePath;
      return {
        onClick: onNavigationToggle,
        ...navItem,
        selected
      };
    })
  }), [cleanedRoute, onNavigationToggle, customerId]);
  const collectionSmartContractsArray = useMemo(() => {
    const collectionSmartContracts = groupToKeyMap(addresses.flatMap(address => (address.cryptoAddressToTokens || []).map(adt => ({
      smartContract: adt.token.collectionSmartContract,
      token: adt.token
    }))), ({
      smartContract
    }) => smartContract.address);
    return Array.from(collectionSmartContracts).map(([collectionAddress, value]) => ({
      collectionAddress,
      value
    }));
  }, [addresses]);
  const collectionNavSection: NavSection = useMemo(() => finalizeNavSection({
    title: "Collections",
    navItems: [...collectionSmartContractsArray.map(collection => ({
      key: `${collection.value[0].smartContract.blockchain}_${collection.value[0].smartContract.address}`,
      label: collection.value[0].smartContract.collectionMetadata.title!,
      badge: <Badge status="new">{`${collection.value.length}`}</Badge>,
      icon: () => <FixedDimensionMedia className="claiming-modal-nft-image" hasBorderRadius={false} size="extraSmall" calculation="cover" src={collection.value[0].smartContract.collectionMetadata.cloudImage} />,
      url: customerId ? `/${customerId}/${stringify({
        collection: collection.value[0].smartContract.address
      }, {
        addQueryPrefix: true
      })}` : `/${stringify({
        collection: collection.value[0].smartContract.address
      }, {
        addQueryPrefix: true
      })}`
    }))]
  }), [collectionSmartContractsArray, finalizeNavSection, customerId]);
  const walletNavSection: NavSection = useMemo(() => finalizeNavSection({
    title: "Wallets",
    navItems: [{
      label: "All Wallets",
      icon: WalletSVGIcon,
      url: customerId ? `/${customerId}` : "/",
      isDefault: true,
      badge: <Badge status="new">
                                {`${addresses.flatMap(({
          cryptoAddressToTokens
        }) => cryptoAddressToTokens).reduce((acc, item) => {
          const quantity = item?.tokenQuantity ? item?.tokenQuantity : BigIntStr.toBigIntStr(0);
          return BigIntStr.sum([acc, quantity]);
        }, BigIntStr.toBigIntStr(0)) || 0}`}
                            </Badge>,
      onClick: () => {
        // for some reason this is needed for base route
        onNavigationToggle();
        if (customerId) {
          customerRoutePush(`/:customerId`, {
            customerId
          });
        } else {
          customerRoutePush("/");
        }
      }
    }, ...addresses.flatMap(({
      cryptoWallets,
      blockchainType,
      address,
      cryptoAddressToTokens
    }) => cryptoWallets.map(cryptoWallet => ({
      address,
      blockchainType,
      cryptoWallet,
      tokenCount: cryptoAddressToTokens.reduce((acc, item) => {
        const quantity = item?.tokenQuantity ? item?.tokenQuantity : BigIntStr.toBigIntStr(0);
        return BigIntStr.sum([acc, quantity]);
      }, BigIntStr.toBigIntStr(0))
    }))).map(({
      address,
      blockchainType,
      cryptoWallet,
      tokenCount
    }) => ({
      key: `${blockchainType}_${address}`,
      label: shortenCryptoAddress(address),
      badge: <Badge status="new">{`${tokenCount || 0}`}</Badge>,
      icon: () => <WalletIcon walletConnectionApp={cryptoWallet.connectionApp} />,
      url: customerId ? `/${customerId}/${stringify({
        address
      }, {
        addQueryPrefix: true
      })}` : `/${stringify({
        address
      }, {
        addQueryPrefix: true
      })}`
    }))]
  }), [addresses, finalizeNavSection, onNavigationToggle, customerId]);
  const filteredAddresses = useMemo(() => {
    if (user?.profileImage && user?.profileImage?.imageType === "nft") {
      return addresses.map(crytoAddress => ({
        ...crytoAddress,
        cryptoAddressToTokens: crytoAddress.cryptoAddressToTokens
      })).flatMap(cryptoAddress => (cryptoAddress.cryptoAddressToTokens || []).map(cryptoAddressToToken => ({
        cryptoAddress,
        cryptoAddressToToken,
        token: cryptoAddressToToken.token,
        collectionMetadata: cryptoAddressToToken.token.collectionSmartContract?.collectionMetadata,
        tokenMetadata: cryptoAddressToToken.token.tokenMetadata
      }))).filter(({
        token
      }) => token.id === user?.profileImage?.token?.id);
    }
    return [];
  }, [addresses, user?.profileImage]);
  return <LeftNavigation mobile={mobile}>
            <div className="novel-config-nav-wrapper">
                <div className="novel-config-nav-container">
                    <Navigation location={customerId ? `/${customerId}` : customerGalleryPrefix}>
                        <div className="flex flex-col nav-column novel-all-nav-items-container">
                            <div className="novel-config-user-container flex-col mb-10">
                                {user?.profileImage && user?.profileImage?.image ? user?.profileImage.imageType && user?.profileImage.imageType === "nft" ? filteredAddresses.length === 1 ? <img src={user?.profileImage?.image} className="roundHex imageCircular cursor-pointer" onClick={() => onClickClaimed(user?.profileImage?.token)} /> : <div className="imageCircular rounded-full cursor-pointer gradientBg" /> : <div className="imageCircular">
                                            <img src={user?.profileImage?.image} className="object-cover imageCircular rounded-full bg-gray-200 cursor-pointer" />
                                        </div> : <div className="imageCircular rounded-full cursor-pointer gradientBg" />}
                                <div className="centeric-side">
                                    <div className="novel-config-user-display-name">
                                        {userDisplayName}
                                    </div>
                                    {user?.emailPublic && <div className="novel-config-user-email">
                                            {user?.primaryEmail}
                                        </div>}
                                    {!customerId && <button onClick={() => {
                  customerRoutePush("/manage");
                  onNavigationToggle();
                }} style={{
                  marginTop: "10px"
                }} className="full-button-outline mobile-only col-grey">
                                            <Box style={{
                    flex: 1,
                    justifyContent: "center",
                    alignItems: "center"
                  }} columnGap={1}>
                                                <CogIcon className="text-black-400 font-semibold w-6" />
                                                Manage{" "}
                                            </Box>
                                        </button>}
                                </div>
                            </div>
                            {walletNavSection.title && <span className="novel-navigation-section-title ">
                                    {walletNavSection.title}
                                </span>}
                            <div className="wallet-nav-section">
                                <Navigation.Section items={walletNavSection.navItems} />
                            </div>
                            {collectionNavSection.title && <span className="novel-navigation-section-title ">
                                    {collectionNavSection.title}
                                </span>}
                            <div style={{
              height: "100%",
              overflow: "auto"
            }}>
                                <Navigation.Section items={collectionNavSection.navItems} />
                            </div>
                        </div>
                        <div className="logo-container">
                            <Image src={Logo} alt="Logo" layout="fill" objectFit="contain" />
                        </div>
                    </Navigation>
                </div>
            </div>
        </LeftNavigation>;
};
export default LeftNavigationPane;